import { css } from '@emotion/react';
import {
  type FC, useCallback,
} from 'react';
import { getAlternatingColor } from '~/_shared/baseComponents/alternatingStripes/alternatingStripes';
import { OverlayLoaderComponent } from '~/_shared/components/overlay/overlayLoader.component';
import { useTheme } from '~/_shared/themes/theme.hooks';
import { type ThemeProps } from '~/_shared/types/themeProps';
import { useTranslation } from '~/_shared/utils/hooks';
import { notNullOrUndefined } from '~/_shared/utils/typeGuards';
import type { ConstraintsValidationDataMap } from '~/sidebar/sidebarApps/mapTools/boundary/loadBoundaryPane/createCustomBoundaryPane/createBoundaryGroupFromAIModal/modalSteps/constraintsValidation/constraints.types';
import { type MetricValue } from '../createBoundaryGroupFromAIModal.types';
import {
  AdditionalOptionsWarningMessageComponent,
  AdditionalWarningMessageType,
} from './additionalOptionsWarningMessage.component';
import { TerritoryOptimizationConstraint } from './territoryOptimizationConstraint.component';

const contentContainerStyle = css({
  position: 'relative',
  padding: '10px 0 0 25px',
  marginTop: 10,
});

const constraintsContainer = ({ theme }: ThemeProps) => css({
  border: `1px solid ${theme.borderColors.primary}`,
  padding: '8px 10px',
  borderRadius: 4,
});

const constraintsItemStyle = ({ theme }: ThemeProps) => css({
  backgroundColor: getAlternatingColor(theme),
  border: `1px solid ${theme.borderColors.primary}`,
  borderRadius: 4,
  marginTop: 25,
  '&:first-of-type': {
    marginTop: 0,
  },
  padding: 8,
});

const constraintsWarningStyle = css({
  marginTop: 15,
});

const selectLabelStyle = css({
  margin: '0 0 5px',
});

const generalErrorStyle = css({
  marginBottom: 10,
});

type AdditionalOptionsConstraintsProps = Readonly<{
  dataMetricsValues: readonly MetricValue[];
  demographicsValues: readonly MetricValue[];
  generalError: string;
  isLoading: boolean;
  validationData: ConstraintsValidationDataMap;

  onChangeDataMetrics: (dataMetricsValues: readonly MetricValue[]) => void;
  onChangeDemographics: (demographicsValues: readonly MetricValue[]) => void;
}>;

export const AdditionalOptionsConstraintsComponent: FC<AdditionalOptionsConstraintsProps> = (props) => {
  const {
    demographicsValues, dataMetricsValues, validationData, onChangeDataMetrics, onChangeDemographics, generalError,
  } = props;
  const [t] = useTranslation();
  const theme = useTheme();

  const handleConstraintChange = useCallback((
    id: string,
    change: {
      isConstraint?: boolean;
      minConstraint?: number;
      maxConstraint?: number;
    },
  ) => {
    const metricsUpdateSets = [
      { metrics: dataMetricsValues, onChange: onChangeDataMetrics },
      { metrics: demographicsValues, onChange: onChangeDemographics },
    ];

    const metricSet = metricsUpdateSets.find(set => set.metrics.find(metric => metric.id === id));
    if (!metricSet) {
      return;
    }

    const { metrics, onChange } = metricSet;

    const newMetricValues = metrics.map(metric => {
      if (metric.id !== id) {
        return metric;
      }

      // Reset min / max on check / uncheck
      if (notNullOrUndefined(change.isConstraint) && change.isConstraint !== metric.isConstraint) {
        return {
          ...metric,
          isConstraint: change.isConstraint,
          constraintLowerBound: change.minConstraint ?? undefined,
          constraintUpperBound: change.maxConstraint ?? undefined,
        };
      }

      return ({
        ...metric,
        constraintLowerBound: change.minConstraint ?? metric.constraintLowerBound,
        constraintUpperBound: change.maxConstraint ?? metric.constraintUpperBound,
      });
    });
    onChange(newMetricValues);
  }, [dataMetricsValues, demographicsValues, onChangeDataMetrics, onChangeDemographics]);

  return (
    <div css={contentContainerStyle}>
      <p css={selectLabelStyle}>{t('Set Constraints')}</p>

      {generalError && (
        <AdditionalOptionsWarningMessageComponent
          css={generalErrorStyle}
          type={AdditionalWarningMessageType.Error}
        >
          {generalError}
        </AdditionalOptionsWarningMessageComponent>
      )}

      <div css={constraintsContainer({ theme })}>
        {[
          ...dataMetricsValues.filter(dataMetricsValue => dataMetricsValue.checked),
          ...demographicsValues.filter(demographicsValue => demographicsValue.checked),
        ].map(metric => {
          const metricValidationData = validationData.get(metric.id);
          const animation = (validationData.size > 1 && metricValidationData) ? (
            metricValidationData.errorMessages.length ? 'error' : 'success'
          ) : undefined;

          return (
            <div
              css={constraintsItemStyle({ theme })}
              key={metric.id}
            >
              <TerritoryOptimizationConstraint
                id={metric.id}
                label={metric.name}
                min={metricValidationData?.min}
                max={metricValidationData?.max}
                minValue={metric.constraintLowerBound}
                maxValue={metric.constraintUpperBound}
                highestMin={metricValidationData?.highestMin}
                lowestMax={metricValidationData?.lowestMax}
                onChange={handleConstraintChange}
                checked={metric.isConstraint}
                animationType={animation}
              />

              {metric.isConstraint && !!metricValidationData?.errorMessages.length && (
                <AdditionalOptionsWarningMessageComponent
                  css={constraintsWarningStyle}
                  type={AdditionalWarningMessageType.Warning}
                >
                  {metricValidationData?.errorMessages.join(' ')}
                </AdditionalOptionsWarningMessageComponent>
              )}
            </div>
          );
        })}
      </div>

      {props.isLoading && (
        <OverlayLoaderComponent />
      )}
    </div>
  );
};
